/**
 * React hook wiring {@link StopMapLayersController} to MapComponent lifecycle and stop popup state.
 */

import type maplibregl from "maplibre-gl";
import { useCallback, useEffect, useRef, useState } from "react";

import type { StopWheelchairBoarding } from "../../../types/dtos/api/stop.dto";
import { StopMapLayersController } from "./StopMapLayersController";
import type { StopClickPayload, StopPopupState } from "./stopMapLayers.types";

const INITIAL_STOP_POPUP_STATE: StopPopupState = {
    "visible":            false,
    "lngLat":             [ 0, 0 ],
    "stopId":             "",
    "stopName":           "",
    "isParentStop":       false,
    "wheelchairBoarding": null,
};

interface UseStopMapLayersOptions {

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Whether the MapLibre map instance has been created. */
    "isMapInitialized": boolean;
}

interface UseStopMapLayersResult {

    /** Attaches stop layers to a map (call after style load). */
    "setupStopLayers": ( map: maplibregl.Map ) => void;

    /** Toggles dev preload bbox / shrink debug overlays. */
    "setStopPreloadDebugVisibility": ( options: {
        "bbox":   boolean;
        "shrink": boolean;
    } ) => void;

    /** Props for {@link StopPopup} excluding map, margins, and isDarkUi. */
    "stopPopupProps": StopPopupState & {
        "onClose":                              () => void;
        "resolveWheelchairBoardingForPlatform": ( platformCode: string ) => StopWheelchairBoarding | null;
    };

}

/**
 * Orchestrates stop map layers and popup state for MapComponent.
 *
 * @param options Theme and map readiness flags.
 * @returns Setup callback and popup render props.
 */
export function useStopMapLayers( options: UseStopMapLayersOptions ): UseStopMapLayersResult {
    const { isDarkUi, isMapInitialized } = options;

    const controllerRef = useRef( new StopMapLayersController() );

    const [ stopPopup, setStopPopup ] = useState<StopPopupState>( INITIAL_STOP_POPUP_STATE );

    const handleStopClick = useCallback( ( payload: StopClickPayload ): void => {
        setStopPopup( {
            "visible":            true,
            "lngLat":             payload.lngLat,
            "stopId":             payload.stopId,
            "stopName":           payload.stopName,
            "isParentStop":       payload.isParentStop,
            "wheelchairBoarding": payload.wheelchairBoarding ?? null,
        } );
    }, [] );

    const resolveWheelchairBoardingForPlatform = useCallback( ( platformCode: string ): StopWheelchairBoarding | null => {
        const parentStopId = stopPopup.stopId;

        if ( parentStopId === "" || platformCode === "" ) {
            return null;
        }

        return controllerRef.current.resolveWheelchairBoardingForPlatform( parentStopId, platformCode );
    }, [ stopPopup.stopId ] );

    const closeStopPopup = useCallback( (): void => {
        setStopPopup( ( previousState ) => ( {
            ...previousState,
            "visible": false,
        } ) );
    }, [] );

    const setupStopLayers = useCallback( ( map: maplibregl.Map ): void => {
        void controllerRef.current.attach( map, {
            "isDarkUi":    isDarkUi,
            "onStopClick": handleStopClick,
        } );
    }, [ handleStopClick, isDarkUi ] );

    const setStopPreloadDebugVisibility = useCallback( ( options: {
        "bbox":   boolean;
        "shrink": boolean;
    } ): void => {
        controllerRef.current.setPreloadDebugVisibility( options );
    }, [] );

    useEffect( () => {
        if ( !isMapInitialized ) {
            return;
        }

        void controllerRef.current.applyTheme( isDarkUi );
    }, [ isDarkUi, isMapInitialized ] );

    useEffect( () => () => {
        controllerRef.current.detach();
    }, [] );

    return {
        setupStopLayers,
        setStopPreloadDebugVisibility,
        "stopPopupProps": {
            ...stopPopup,
            "onClose": closeStopPopup,
            resolveWheelchairBoardingForPlatform,
        },
    };
}
